import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  StateKey,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core';
import { Title } from '@angular/platform-browser';

import { BehaviorSubject, Observable, Subject, race, timer, zip, finalize, map, take, tap } from 'rxjs';
import sha256 from 'sha256';
import { Md5 } from 'ts-md5';

import { environment } from '@env/environment';

import { UniversalStorageService } from '@personal-area/storage';
import { PwaDetectorService } from '@personal-area/global/browser-detection';
import { Role, UserDepositInfo, LoggedUser } from '@personal-area/global/users/models';

import {
  DataLayer,
  DataLayerPushEvent,
  EventType,
  GA4Event,
  GA360Event,
  PersonType,
} from '../models/data-layer.model';

const gaCookie = '_ga';
const tsPageViewKey = 'transfer-page-view';

declare let dataLayer: any;

export const getPersonTypeFromUser = (
  user: LoggedUser = {} as LoggedUser,
): PersonType => {
  return {
    trader: user.isTrader || user.role === Role.Trader,
    partner: user.isPartner || user.role === Role.Partner,
    tp: user.isTradingPlatform || user.role === Role.TradingPlatform,
    investor: user.isInvestor || user.role === Role.Investor,
    exchanger: user.isExchanger || user.role === Role.Exchanger,
  };
};

export const getPersonTypeFromRole = (role: Role): PersonType => {
  return {
    trader: role === Role.Trader,
    partner: role === Role.Partner,
    tp: role === Role.TradingPlatform,
    investor: role === Role.Investor,
    exchanger: role === Role.Exchanger,
  };
};

const stopWaiting = 3000; // in milliseconds

/* Google tag manager service
 * 1) Все события должны добавляться через dataLayer.push({...})
 * 2) Для добавления Pageview-event использовать метод page()
 * 3) Данные юзера, добавленные через setter data() не будут видны в google tag manager.
 *    Поэтому нужно использовать dataLayer.push, чтобы они попали как ивент
 * 4) Параметры юзера проставляюся в AppComponent, ngOnInit(), по возможности - проставлять их там.
 * 5) Все обновления состояния пушим при Pageview. Так в GTM в последнем Pageview будет актуальное состояние
 */
@Injectable({ providedIn: 'root' })
export class GTMService {
  private readonly _platformId = inject(PLATFORM_ID);
  public event$: Subject<DataLayerPushEvent> =
    new Subject<DataLayerPushEvent>();

  public static id = 'gtm';

  private static _trackingId: string;
  private static _gtmLoadingTime: number;

  private static _defaultData = {
    user: {},
    regulated: 'IFSC',
  };

  public isEnabled: boolean;
  public afterPageView: BehaviorSubject<string>;

  private _waitFactors: Observable<unknown>[];
  private _waitingQueue: string[] = [];
  private readonly _gaEndpoint =
    'https://us-central1-fbs-ga360.cloudfunctions.net/paylog';
  private _data: DataLayer;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _transferState: TransferState,
    private _cookieService: UniversalStorageService,
    private _title: Title,
    private _pwaDetector: PwaDetectorService,
  ) {
    this.isEnabled = environment.gtm.enable;
    if (this.isEnabled) {
      this.afterPageView = new BehaviorSubject<string>('');
      if (isPlatformBrowser(this._platformId)) {
        dataLayer = dataLayer || [];
      }
      this.data = GTMService._defaultData;
      this._waitFactors = [];
    }
  }

  /**
   * Merge DataLayer params
   * @param val
   */
  set data(val: any) {
    this._data = { ...this._data, ...val };
    for (const prop in this._data) {
      if (this._data[prop] === undefined) {
        delete this._data[prop];
      }
    }
  }

  public get data(): any {
    return this._data;
  }

  /**
   * @returns {string}
   */
  public get currentGoogleClientId(): string {
    if (this._cookieService.check(gaCookie)) {
      const parts = this._cookieService.getItem(gaCookie).split('.');
      if (parts.length > 3) {
        return parts[2] + '.' + parts[3];
      }
    }
    return undefined;
  }

  set userData(val: any) {
    if (!this._data) {
      this.data = GTMService._defaultData;
    }
    this._data.user = { ...this._data.user, ...val };
  }

  /**
   * @param {string} trackingId
   */
  public static getScript(trackingId: string): string {
    return (
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
      `new Date().getTime(),event:'gtm.js',regulated:'${GTMService._defaultData.regulated}'});var f=d.getElementsByTagName(s)[0],` +
      `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
      `'https://${environment.gtm.domain}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
      `})(window,document,'script','dataLayer','` +
      trackingId +
      `');`
    );
  }

  /**
   * Initialize GTM session
   * @param {string} trackingId
   */
  public static createGTMSession(trackingId: string): void {
    if (!document.getElementById(GTMService.id)) {
      const s = document.createElement('script');

      GTMService._trackingId = trackingId;
      GTMService._gtmLoadingTime = new Date().valueOf();
      s.id = GTMService.id;
      s.innerHTML = GTMService.getScript(trackingId);
      document.head.insertBefore(s, document.head.firstChild);
    }
  }

  /**
   * Measurement Google API
   * @param search
   * @param user
   */
  public collectGA(search: string, user: LoggedUser): void {
    if (this.isEnabled && isPlatformBrowser(this._platformId)) {
      const params = {
        uid: user.id,
        country: user.country,
        time: (Date.now() / 1000).toFixed(0),
        query: search.toLowerCase(),
      };
      const payload = Object.keys(params)
        .map((k) => `${k}=${encodeURIComponent(params[k])}`)
        .join('&');

      // Avoid CORS, the response body is unavailable
      fetch(this._gaEndpoint, {
        method: 'POST',
        body: payload,
        mode: 'no-cors',
      });
    }
  }

  /**
   * Sets basic user fields
   */
  public setUser(user: LoggedUser, country: string): void {
    const phone = user.phoneCode + user.phoneNumber;
    const stat = user.extras ? user.extras.depositInfo : new UserDepositInfo();

    this.isEnabled =
      environment.gtm.enable && (!user.isTest || environment.isStage);

    this.userData = {
      email_md5: <string>Md5.hashStr(user.email),
      email: user.email,
      name: user.name,
      id: user.id,
      auth_status: '1',
      country: user.country,
      fb_phone_number: user.phoneNumber ? sha256(phone) : '',
      fb_country: country ? sha256(country) : '',
      client_id: user.clientId,
      language: (user.language.includes('-')
        ? user.language.split('-')[0]
        : user.language
      ).toLowerCase(),
      verifyemail: user.isEmailConfirmed ? '1' : '0',
      verifyphone: user.isPhoneConfirmed ? '1' : '0',
      tracking: {
        source:
          user.utmData && user.utmData.source ? user.utmData.source : 'none',
        medium:
          user.utmData && user.utmData.medium ? user.utmData.medium : 'none',
      },
      finances: {
        transactionQuantity: stat.depositsCount,
      },
      rank: user.extras && user.extras.rank >= 0 ? user.extras.rank : null,
    };

    this.setPersonType(getPersonTypeFromUser(user));

    // удаляем поля с пустыми значениями
    Object.keys(this._data.user).forEach((key) => {
      if (
        !this._data.user[key] ||
        (typeof this._data.user[key] === 'object' &&
          Object.keys(this._data.user[key]).length === 0)
      ) {
        delete this._data.user[key];
      }
    });
  }

  /**
   * Устанавливает personType для GTM с учётом данных из куки personType
   * @param personType
   */
  public setPersonType(personType: PersonType): void {
    this.userData = {
      person_type: personType,
    };
  }

  /**
   * Добавить триггер на ожидание отправки
   * @param factor$
   */
  public addWaitFactor(factor$: Observable<unknown>): void {
    if (this.isEnabled) {
      this._waitFactors.push(factor$);
    }
  }

  /**
   * Navigate event handler
   * @param {string} path
   */
  public page(path: string): void {
    if (this.isEnabled && typeof dataLayer !== 'undefined' && dataLayer) {
      const onPageSend = () => {
        this._waitingQueue.forEach((item) => this._pushPageView(item));
        this._waitingQueue = [];
      };

      this._waitingQueue.push(path);
      if (this._waitFactors && this._waitFactors.length > 0) {
        const stopWaitingFactor$ = timer(stopWaiting).pipe(map(() => []));
        race(stopWaitingFactor$, zip(...this._waitFactors))
          .pipe(
            take(1),
            tap({
              next: onPageSend,
              error: onPageSend,
            }),
            finalize(() => {
              this._waitFactors = [];
            }),
          )
          .subscribe();
      } else {
        onPageSend();
      }
    }
  }

  public serverPage(renderer: Renderer2, path: string): void {
    if (this.isEnabled) {
      if (this._waitFactors && this._waitFactors.length > 0) {
        const stopWaitingFactor$ = timer(stopWaiting).pipe(map(() => []));
        race(stopWaitingFactor$, zip(this._waitFactors))
          .pipe(
            take(1),
            finalize(() => {
              this._pushServerPageView(renderer, path);
              this._waitFactors = [];
            }),
          )
          .subscribe();
      } else {
        this._pushServerPageView(renderer, path);
      }
    }
  }

  /**
   * GTM event handler
   */
  public event(event: EventType, data: GA360Event | GA4Event = {}): void {
    if (this.isEnabled && typeof dataLayer !== 'undefined' && dataLayer) {
      this.event$.next({ event, data });
      dataLayer.push({
        ...data,
        event: event.toString(),
      });
    }
  }

  /**
   * Удалить данные по пользователю
   */
  public clearData(): void {
    this._data = undefined;
  }

  private _pushServerPageView(renderer: Renderer2, path: string): void {
    const head = this._data || GTMService._defaultData;

    const pageViewEvent = {
      event: EventType.PageView,
      page: {
        path: '{{ $path }}',
        title: this._title.getTitle(),
      },
    };

    const script = JSON.stringify({
      ...head,
      ...pageViewEvent,
    }).replace('{{ $path }}"', path + '" + window.location.search');

    const s = renderer.createElement('script');
    s.id = 'pageview';
    s.innerHTML = `(function() {
      dataLayer.push(${script});
    })()`;
    renderer.appendChild(this._document.head, s);

    this._transferState.set(makeStateKey<string>(tsPageViewKey), path);
  }

  private _pushPageView(path: string): void {
    if (
      window['google_tag_manager'] &&
      window['google_tag_manager'][GTMService._trackingId] &&
      window['google_tag_manager'][GTMService._trackingId]['dataLayer']
    ) {
      window['google_tag_manager'][GTMService._trackingId]['dataLayer'].reset();
      while (dataLayer.length > 0) {
        (<unknown[]>dataLayer).shift();
      }
    }

    const pageViewKey: StateKey<string> = makeStateKey<string>(tsPageViewKey);
    const serverPath = this._transferState.get(pageViewKey, '');
    if (serverPath === path.split('?')[0]) {
      this._transferState.remove(pageViewKey);
      this.afterPageView.next(path);
      return;
    }

    const head = this._data || GTMService._defaultData;
    const pageViewEvent: Omit<DataLayer, 'user' | 'regulated'> = {
      event: EventType.PageView,
      page: {
        isPwa: this._pwaDetector.isInPwaMode(),
        path,
        title: this._title.getTitle(),
        referrer: this._document.referrer,
      },
      userAgent: this._document.defaultView.navigator.userAgent,
      isOnline: this._document.defaultView.navigator.onLine,
    };
    dataLayer.push({
      ...head,
      ...pageViewEvent,
    });

    this.afterPageView.next(path);
  }
}
